// The control API, typed. Every call is same-origin behind Caddy; the session
// cookie is HttpOnly, so there is no token to manage here.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let body: any = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // A proxy error page or an HTML fallthrough is not JSON; keep the status
    // line as the message rather than throwing a bare SyntaxError.
  }

  if (!response.ok) {
    // The API always shapes errors as {error: {code, message}}; fall back to
    // the status line so an unexpected 502 from the proxy still says something.
    const message = body?.error?.message || body?.detail || `HTTP ${response.status}`;
    throw new ApiError(response.status, message, body?.error?.detail);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// --- shared shapes ---------------------------------------------------------
export interface Page<T> {
  total: number;
  items: T[];
  limit: number;
  offset: number;
}

export type Role = "nci_admin" | "nci_ops" | "reseller";

export interface Me {
  id: number;
  email: string;
  full_name: string | null;
  role: Role;
  reseller_id: number | null;
  reseller_name: string | null;
  totp_enabled: boolean;
}

export type EndpointState =
  | "inventory"
  | "provisioned"
  | "active"
  | "suspended"
  | "revoked"
  | "retired";

export interface EndpointSummary {
  id: string;
  service_tag: string;
  hw_manufacturer: string | null;
  hw_model: string | null;
  state: EndpointState;
  release_channel: "stable" | "beta" | "dev";
  reseller_id: number | null;
  reseller_name: string | null;
  site_id: number | null;
  site_name: string | null;
  agent_version: string | null;
  applied_config_version: number | null;
  desired_config_version: number | null;
  last_checkin_at: string | null;
  seconds_since_checkin: number | null;
  online: boolean;
  config_pending: boolean;
  wg_ip: string | null;
  cpu_pct: number | null;
  channel_count: number;
  sources_ok: number;
  sources_total: number;
  on_backup_origin: number;
  worst_seg_fetch_ms: number | null;
  alert_count: number;
}

export interface Entitlement {
  id: number;
  channel_id: number;
  channel_number: number | null;
  channel_name: string | null;
  profile: "hd" | "sd";
  added_at: string;
  nominal_kbps: number | null;
}

export interface Output {
  id: number;
  channel_id: number | null;
  channel_name: string | null;
  type: "srt" | "hls" | "qam";
  params_json: Record<string, unknown>;
  enabled: boolean;
}

export interface Nic {
  name?: string;
  mac?: string;
  pci?: string;
  driver?: string;
  speed_mbps?: number;
  link?: boolean;
  addrs?: string[];
  default_route?: boolean;
}

export interface Inventory {
  system?: { manufacturer?: string; model?: string; service_tag?: string; bios?: string; uuid?: string };
  cpu?: { model?: string; sockets?: number; cores?: number; threads?: number; base_mhz?: number };
  memory?: { total_mb?: number; dimms?: { slot?: string; size_mb?: number; type?: string; speed?: number }[] };
  storage?: { dev?: string; model?: string; serial?: string; size_gb?: number; bus?: string; smart_ok?: boolean }[];
  nics?: Nic[];
  gpus?: { pci?: string; model?: string; vendor?: string }[];
  psu?: { slot?: number; status?: string }[];
  warnings?: string[];
}

export interface SourceHealth {
  channel_id?: number;
  number?: number;
  name?: string;
  profile?: string;
  state?: string;
  origin?: string;
  seg_fetch_ms_p95?: number;
  stalls_15m?: number;
  playlist_age_s?: number;
  mbps?: number;
  last_http_status?: number;
  backup_healthy?: boolean;
}

export interface OutputHealth {
  output_id?: number;
  type?: string;
  channel_id?: number;
  state?: string;
  out_mbps?: number;
  restarts?: number;
  mcast?: string;
}

export interface Capability {
  cores: number;
  memory_mb: number;
  storage_gb: number;
  nic_count: number;
  has_assist_gpu: boolean;
  cores_per_channel: number;
  estimated_qam_channels: number;
  planned_qam_channels: number;
  planned_profile: string;
  warnings: string[];
  ok: boolean;
}

export interface EndpointDetail extends EndpointSummary {
  hardware_json: Inventory;
  hardware_hash: string | null;
  hardware_updated_at: string | null;
  nic_roles_json: { wan?: { mac?: string }; lan?: { mac?: string; mode?: string; addr?: string; gateway?: string } };
  health_json: {
    sources?: SourceHealth[];
    outputs?: OutputHealth[];
    wireguard?: { up?: boolean; last_handshake_s?: number };
    // Shape lives in EndpointVitals, and is `unknown` here on purpose: an agent
    // newer than this dashboard reports sensors it has never heard of, and the
    // page must render what it recognises rather than fail on what it does not.
    telemetry?: unknown;
    at?: string;
  };
  hls_serve_json: Record<string, number>;
  default_profile: "hd" | "sd";
  mem_pct: number | null;
  disk_pct: number | null;
  temp_c: number | null;
  uptime_s: number | null;
  wg_last_handshake_s: number | null;
  media_jwt_kid: string | null;
  first_active_at: string | null;
  provisioned_at: string | null;
  suspended_at: string | null;
  revoked_at: string | null;
  poll_interval_s: number | null;
  notes: string | null;
  entitlements: Entitlement[];
  outputs: Output[];
  capability: Capability | null;
  bandwidth_estimate: { nominal_kbps: number; estimated_kbps: number; estimated_mbps: number; channels: number } | null;
}

export interface Site {
  id: number;
  reseller_id: number;
  reseller_name: string | null;
  code: string | null;
  name: string;
  customer_name: string | null;
  address: string | null;
  contact_json: Record<string, unknown>;
  notes: string | null;
  created_at: string;
  endpoint_count: number | null;
}

export interface AppUser {
  id: number;
  email: string;
  full_name: string | null;
  role: Role;
  reseller_id: number | null;
  reseller_name: string | null;
  is_active: boolean;
  totp_enabled: boolean;
  totp_required_by_policy: boolean;
  last_login_at: string | null;
  created_at: string | null;
  /** Returned exactly once, on create and on reset. Never readable again. */
  initial_password?: string | null;
}

export interface Reseller {
  id: number;
  name: string;
  contact_email: string | null;
  billing_email: string | null;
  phone: string | null;
  status: string;
  channel_catalog: number[];
  endpoint_count: number | null;
  site_count: number | null;
  user_count?: number | null;
  require_totp?: boolean;
}

export interface Channel {
  id: number;
  number: number;
  name: string;
  logo_url: string | null;
  urls_json: { hd?: string[]; sd?: string[] };
  nominal_kbps_json: { hd?: number; sd?: number };
  scte35_passthrough: boolean;
  status: "active" | "retired";
  notes: string | null;
  endpoint_count: number | null;
}

export interface DeployJob {
  id: number;
  endpoint_id: string | null;
  target_ip: string;
  ssh_user: string;
  state: "running" | "succeeded" | "failed" | "cancelled";
  repair_mode: boolean;
  expected_service_tag: string | null;
  discovered_service_tag: string | null;
  site_id: number | null;
  steps_json: { step: string; title: string; state: string; detail?: string }[];
  discovered_hardware_json: Inventory | null;
  capability_json: Capability | null;
  suggested_wan_mac: string | null;
  nic_roles_json: Record<string, unknown> | null;
  error: string | null;
  started_by: string | null;
  started_at: string;
  finished_at: string | null;
  log_text: string | null;
}

export interface Alert {
  id: number;
  key: string;
  severity: "P1" | "P2" | "P3";
  state: "firing" | "acknowledged" | "resolved";
  endpoint_id: string | null;
  service_tag: string | null;
  channel_id: number | null;
  summary: string;
  detail_json: Record<string, unknown>;
  count: number;
  first_seen_at: string;
  last_seen_at: string;
  acknowledged_by: string | null;
}

export interface Release {
  id: number;
  component: string;
  version: string;
  artifact_url: string | null;
  image_tag: string | null;
  sha256: string | null;
  channel: "stable" | "beta" | "dev";
  rollout_pct: number;
  halted: boolean;
  notes: string | null;
  promoted_by: string | null;
  promoted_at: string | null;
  created_at: string;
}

export interface Command {
  id: number;
  endpoint_id: string;
  type: string;
  params_json: Record<string, unknown>;
  state: string;
  created_by: string | null;
  created_at: string;
  delivered_at: string | null;
  completed_at: string | null;
  ok: boolean | null;
  result_json: Record<string, unknown> | null;
}

export interface OriginLoad {
  dvrs: {
    dvr_host: string;
    reported_sessions: number;
    reported_mbps: number;
    sessions_on_backup: number;
    authorised_sessions: number;
    auth_hits_2h: number;
    discrepancy: number;
    burn: "ok" | "warn" | "critical";
  }[];
  total_sessions: number;
  total_mbps: number;
  denies_2h: Record<string, number>;
  recommendation: string;
  thresholds: { warn_mbps: number; critical_mbps: number; warn_sessions: number };
  auth_probe: {
    dvr_host: string;
    passed: boolean;
    stream_name: string | null;
    tokenless_status: number | null;
    bad_token_status: number | null;
    detail: string | null;
    at: string;
  }[];
}

export interface Overview {
  fleet: {
    total: number;
    by_state: Record<string, number>;
    online: number;
    offline: number;
    on_backup: number;
    config_pending: number;
    sources_ok: number;
    sources_total: number;
    agent_versions: Record<string, number>;
  };
  alerts: Record<string, number>;
  // NCI only; absent for resellers.
  origins?: Omit<OriginLoad, "auth_probe">;
}

export interface Invoice {
  id: number;
  number: string | null;
  reseller_id: number;
  reseller_name: string | null;
  period_start: string;
  period_end: string;
  state: "draft" | "final" | "void";
  currency: string;
  subtotal: string;
  total: string;
  paid: string | null;
  balance: string | null;
  due_date: string | null;
  finalized_by: string | null;
  finalized_at: string | null;
  emailed_at: string | null;
  notes: string | null;
  lines: {
    id: number;
    endpoint_id: string | null;
    description: string;
    qty: string;
    unit_price: string;
    amount: string;
    manual: boolean;
    detail_json: Record<string, unknown>;
  }[];
  payments: { id: number; amount: string; method_note: string | null; reference: string | null; recorded_by: string | null; at: string }[];
}

export interface Rate {
  id: number;
  endpoint_id: string;
  monthly_rate: string;
  per_channel_addon_rate: string | null;
  currency: string;
  effective_from: string;
  set_by: string | null;
  note: string | null;
  created_at: string;
}
